import React, { useState, useEffect, useRef } from "react";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";
import { Helmet } from "react-helmet";

// Question Type Definitions
interface BaseQuestion {
    type: string;
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    correct: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[]; // Fill-in options for user to choose
    correct: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    correct: boolean;
}

interface MatchThePairQuestion extends BaseQuestion {
    type: "match";
    options: string[];
    labeled: { "A": string[], "B": string[] };
    correct: string;
}

// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion
    | MatchThePairQuestion;

// Define types
type SyllabusType =
    | "All Chapters"


interface MultipleChoiceQuestion {
    question: string;
    options: string[];
    correct: string;
    syllabus?: SyllabusType;
}



const syllabusFiles: Record<SyllabusType, string> = {
    "All Chapters": "GoodHabbitsforLKG.json"
}

const GoodHabbits: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {
        const topics = Object.keys(syllabusFiles) as SyllabusType[];
        const randomTopic = topics[Math.floor(Math.random() * topics.length)];
        const filename = syllabusFiles[randomTopic];

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "/" + filename
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/${filename}`
                );
                data = module.default;
            }
            setQuestions(shuffleArray(data));
            const choosedOne = data[Math.floor(Math.random() * data.length)];
            setCurrentQuestion(choosedOne);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.correct;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false" || currentQuestion.type === "match") {
            correctAnswer = currentQuestion.correct;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                setShowImage(false);
                loadRandomSyllabusQuestions();
                setSelectedOption(null);
            }, 4000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Good Habits and Manners Worksheets for LKG | Daily Routines for Kids";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach LKG kids about good habits and basic manners with simple worksheets. Learn daily routines like brushing, bathing, saying thank you, and keeping clean with fun activities." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Good Habits and Manners</h1>

                            <p>
                                Teaching <strong>good habits and manners</strong> from a young age helps children grow into respectful and responsible individuals. These worksheets are specially designed for <strong>LKG students</strong> to introduce basic personal hygiene, kind behavior, and polite expressions through simple activities and illustrations.
                            </p>

                            <h2>🌞 Why Teach Good Habits in LKG?</h2>
                            <p>
                                Early years are crucial for character building. With these worksheets, children can:
                                <ul>
                                    <li>Learn everyday habits like brushing teeth, bathing, and washing hands</li>
                                    <li>Understand simple manners like saying <strong>“please,” “sorry,” and “thank you”</strong></li>
                                    <li>Develop a sense of responsibility for cleanliness and kindness</li>
                                </ul>
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify and practice daily hygiene routines</li>
                                <li>Learn polite words and greetings</li>
                                <li>Differentiate between good and bad habits</li>
                                <li>Encourage helpful and kind behavior at home and school</li>
                            </ul>

                            <h2>📋 Worksheet Activities</h2>
                            <ul>
                                <li>Circle the pictures showing good habits</li>
                                <li>Match habits to daily routines (<strong>e.g. Brushing → Morning</strong>)</li>
                                <li>Choose the correct behavior from a set of images</li>
                                <li>Color the good habits</li>
                                <li>True or false statements about habits and manners</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Be a role model by practicing good habits daily</li>
                                <li>Create a routine chart at home or in class</li>
                                <li>Appreciate and encourage children when they display good manners</li>
                                <li>Use stories, rhymes, and visual charts to reinforce learning</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/general-awareness/people-who-help-us"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>People Who Helps Us</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/general-awareness/seasons"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Seasons</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/general-awareness/transports"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Transports</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Make learning fun with songs, storytelling, and movement-based games. The more they move and point, the better they learn!
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Good Habits and Manners</h1>

                        <p>
                            Teaching <strong>good habits and manners</strong> from a young age helps children grow into respectful and responsible individuals. These worksheets are specially designed for <strong>LKG students</strong> to introduce basic personal hygiene, kind behavior, and polite expressions through simple activities and illustrations.
                        </p>

                        <h2>🌞 Why Teach Good Habits in LKG?</h2>
                        <p>
                            Early years are crucial for character building. With these worksheets, children can:
                            <ul>
                                <li>Learn everyday habits like brushing teeth, bathing, and washing hands</li>
                                <li>Understand simple manners like saying <strong>“please,” “sorry,” and “thank you”</strong></li>
                                <li>Develop a sense of responsibility for cleanliness and kindness</li>
                            </ul>
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Identify and practice daily hygiene routines</li>
                            <li>Learn polite words and greetings</li>
                            <li>Differentiate between good and bad habits</li>
                            <li>Encourage helpful and kind behavior at home and school</li>
                        </ul>

                        <h2>📋 Worksheet Activities</h2>
                        <ul>
                            <li>Circle the pictures showing good habits</li>
                            <li>Match habits to daily routines (<strong>e.g. Brushing → Morning</strong>)</li>
                            <li>Choose the correct behavior from a set of images</li>
                            <li>Color the good habits</li>
                            <li>True or false statements about habits and manners</li>
                        </ul>

                        <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                        <ul>
                            <li>Be a role model by practicing good habits daily</li>
                            <li>Create a routine chart at home or in class</li>
                            <li>Appreciate and encourage children when they display good manners</li>
                            <li>Use stories, rhymes, and visual charts to reinforce learning</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/lkg/general-awareness/people-who-help-us"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>People Who Helps Us</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/lkg/general-awareness/seasons"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Seasons</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/lkg/general-awareness/transports"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Transports</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🌟 Mama’s Guidance Tip</h2>
                        <p>
                            Make learning fun with songs, storytelling, and movement-based games. The more they move and point, the better they learn!
                        </p>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.correct.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.correct).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                            {currentQuestion.type === "match" && (
                                <>
                                    <table className="table-bordered table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Column A</th>
                                                <th scope="col">Column B</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentQuestion.labeled.A.map((leftItem, index) => (
                                                <tr key={index}>
                                                    <td>{leftItem}</td>
                                                    <td>{currentQuestion.labeled.B[index]}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.correct.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                </>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default GoodHabbits;
